import type { DriverTopUpInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input } from 'antd';
import { Banknote } from 'lucide-react';
import { useState } from 'react';
import { parseMoneyAmount } from '../../../shared/policies/parse-money-amount';
import { useTripTopUp } from '../hooks/use-trip-top-up';

interface TopUpFormValues {
  amount: string;
  note: string;
}

/** More cash for the driver once the trip is out. KES only, like the first advance; the amount and who it is for are what the ledger records. */
export function DispatchTopUp({ tripId, driverName, onGiven }: { tripId: string; driverName: string; onGiven: () => void }) {
  const [form] = Form.useForm<TopUpFormValues>();
  const [isOpen, setIsOpen] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const { giveTopUp, isSubmitting, error, clearError } = useTripTopUp(tripId, onGiven);

  function close() {
    clearError();
    setIsOpen(false);
  }

  async function submit(values: TopUpFormValues) {
    const amount = parseMoneyAmount(values.amount, 'KES');
    if (!amount.ok) return;
    const input: DriverTopUpInput = { amount: { amountMinor: amount.amountMinor, currency: 'KES' }, note: values.note.trim(), idempotencyKey };
    if (!(await giveTopUp(input))) return;
    form.resetFields();
    // A refusal keeps the key so a retry of the same request replays; only a recorded top-up starts a new one.
    setIdempotencyKey(crypto.randomUUID());
    close();
  }

  return (
    <section aria-label="Top-up" className="flex items-center justify-between rounded-lg border border-line bg-surface p-5">
      <div>
        <h2 className="text-base font-semibold text-fg">Top-up</h2>
        <p className="text-sm text-muted">Give {driverName} more cash while the trip is out.</p>
      </div>
      <Button icon={<Banknote className="size-4" />} onClick={() => setIsOpen(true)}>
        Give a top-up
      </Button>
      <Drawer open={isOpen} onClose={close} title="Give a top-up" placement="right" size={480} destroyOnClose>
        {error && <Alert className="mb-5" type="error" title={error} showIcon />}
        <Form<TopUpFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={isSubmitting}>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[
              {
                validator: (_, value?: string) => {
                  const check = parseMoneyAmount(value, 'KES');
                  return check.ok ? Promise.resolve() : Promise.reject(new Error(check.reason));
                },
              },
            ]}
          >
            <Input inputMode="decimal" prefix="KES" />
          </Form.Item>
          <Form.Item name="note" label="What is it for?" rules={[{ required: true, whitespace: true, message: 'Say what this is for' }, { max: 200 }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item className="mb-0 pt-2">
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              Give top-up
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </section>
  );
}
